import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../ngrx/auth/auth.actions';
import { tap, mergeMap, filter } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../services/auth/auth.service';
import { NativeAuthStorageService } from '../auth/native-auth-storage.service';
import { firebaseConfig } from '../../firebase-init';
import { environment } from '../../environment/environment';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, type NotificationReceivedEvent } from '@capacitor-firebase/messaging';

type FcmNotificationPayload = {
  title?: string;
  body?: string;
  data?: Record<string, string>;
};

export type PushPlatform = 'web' | 'ios' | 'android';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private readonly API_URL = `${environment.apiUrl}`;
  private messagingInitialized = false;
  private listenersRegistered = false;
  private apnsTokenReady = false;
  private apnsTokenWaiters: Array<(ready: boolean) => void> = [];

  constructor(
    private http: HttpClient,
    private store: Store,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private nativeAuthStorage: NativeAuthStorageService
  ) {
    this.registerNotificationListeners();
    this.registerServiceWorkerPushDebugBridge();

    this.authService.isAuthenticated$.pipe(
      filter(isAuthenticated => isAuthenticated === true)
    ).subscribe(() => {
      this.initializeMessaging();
    });
  }

  getPlatform(): PushPlatform {
    if (Capacitor.isNativePlatform()) {
      return Capacitor.getPlatform() as 'ios' | 'android';
    }
    return 'web';
  }

  private async waitForServiceWorker(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('[FCM] Service workers not supported');
      return false;
    }

    try {
      if (navigator.serviceWorker.controller) {
        return true;
      }

      const registration =
        (await navigator.serviceWorker.getRegistration('/')) ??
        (await navigator.serviceWorker.getRegistration('/combined-sw.js'));
      if (!registration) {
        console.warn('[FCM] No service worker registration found');
        return false;
      }

      if (registration.active) {
        return true;
      }

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[FCM] Service worker activation timeout');
          resolve(false);
        }, 10000);

        const stateChangeHandler = (reg: ServiceWorkerRegistration) => {
          if (reg.active) {
            clearTimeout(timeout);
            resolve(true);
          }
        };

        if (registration.installing) {
          registration.installing.addEventListener('statechange', () => stateChangeHandler(registration));
        } else if (registration.waiting) {
          registration.waiting.addEventListener('statechange', () => stateChangeHandler(registration));
        }

        navigator.serviceWorker.addEventListener('controllerchange', () => {
          clearTimeout(timeout);
          resolve(true);
        }, { once: true });
      });
    } catch (error) {
      console.error('[FCM] Error waiting for service worker:', error);
      return false;
    }
  }

  private async requestNotificationPermission(): Promise<boolean> {
    try {
      const platform = this.getPlatform();

      if (platform === 'web') {
        if (!('Notification' in window)) {
          console.warn('[FCM] Notifications not supported');
          return false;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.warn('[FCM] Notification permission not granted');
          return false;
        }

        return true;
      }

      const result = await FirebaseMessaging.requestPermissions();
      return result.receive === 'granted';
    } catch (error) {
      console.error('[FCM] Error requesting notification permission:', error);
      return false;
    }
  }

  private registerServiceWorkerPushDebugBridge(): void {
    if (this.getPlatform() !== 'web' || !('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type !== 'FCM_PUSH_DEBUG') {
        return;
      }

      this.logPushReceived('service-worker.background', event.data.payload);
    });
  }

  private logPushReceived(source: string, payload: unknown): void {
    console.log(`[FCM] Push modtaget (${source})`, {
      platform: this.getPlatform(),
      timestamp: new Date().toISOString(),
      payload,
    });
  }

  private isSilentPushData(data?: Record<string, string>): boolean {
    const type = data?.['type'];
    return type === 'token_refresh' || type === 'token_validation';
  }

  private normalizePushData(data: unknown): Record<string, string> | undefined {
    if (!data || typeof data !== 'object') {
      return undefined;
    }

    const normalized: Record<string, string> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (value != null) {
        normalized[key] = String(value);
      }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private extractNotificationPayload(event: NotificationReceivedEvent): FcmNotificationPayload {
    const notification = event.notification;
    const data = this.normalizePushData(notification.data);

    return {
      title: notification.title ?? data?.['title'] ?? 'DUNA',
      body: notification.body ?? data?.['body'] ?? '',
      data,
    };
  }

  private presentForegroundNotification(payload: FcmNotificationPayload): void {
    const title = payload.title ?? 'DUNA';
    const body = payload.body ?? '';

    if (this.getPlatform() === 'web') {
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(title, {
            body,
            icon: '/assets/icons/icon-72.webp',
            data: payload.data,
          });
        } catch (error) {
          console.warn('[FCM] Kunne ikke vise browser Notification API:', error);
        }
      }
    } else if (this.getPlatform() === 'android') {
      // Android viser ikke system-banner i forgrunden — kun via listener (se Capacitor Firebase docs).
      console.warn(
        '[FCM] Android forgrund: push modtaget i app. Systemnotifikation vises når app er i baggrunden.'
      );
    }

    if (title || body) {
      this.snackBar.open(body ? `${title}: ${body}` : title, 'OK', {
        duration: 8000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }
  }

  private registerNotificationListeners(): void {
    if (this.listenersRegistered) {
      return;
    }

    FirebaseMessaging.addListener('notificationReceived', (event) => {
      const payload = this.extractNotificationPayload(event);
      this.logPushReceived('FirebaseMessaging.notificationReceived', event);

      if (this.isSilentPushData(payload.data)) {
        console.log('[FCM] Stille push ignoreret (token_refresh/token_validation)');
        return;
      }

      this.presentForegroundNotification(payload);
    });

    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      this.logPushReceived('FirebaseMessaging.notificationActionPerformed', event);

      const data = event.notification?.data as Record<string, string> | undefined;
      const link = data?.['link'] || data?.['click_action'];
      if (link) {
        window.location.href = link;
      }
    });

    FirebaseMessaging.addListener('tokenReceived', (event) => {
      console.log('[FCM] tokenReceived', {
        platform: this.getPlatform(),
        tokenPreview: `${event.token.slice(0, 12)}…`,
      });
      this.registerTokenWithServer(event.token).catch(error => {
        console.error('[FCM] Token re-registration failed:', error);
      });
    });

    if (this.getPlatform() === 'ios') {
      FirebaseMessaging.addListener('apnsTokenReceived', () => {
        this.apnsTokenReady = true;
        this.resolveApnsWaiters(true);
      });
    }

    this.listenersRegistered = true;
  }

  private resolveApnsWaiters(ready: boolean): void {
    const waiters = this.apnsTokenWaiters.splice(0);
    waiters.forEach(waiter => waiter(ready));
  }

  private waitForApnsToken(timeoutMs = 20000): Promise<boolean> {
    if (this.getPlatform() !== 'ios' || this.apnsTokenReady) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      const timer = window.setTimeout(() => {
        this.apnsTokenWaiters = this.apnsTokenWaiters.filter(waiter => waiter !== onReady);
        console.warn('[FCM] APNS token was not received. Enable Push Notifications in Xcode and upload an APNs key to Firebase.');
        resolve(false);
      }, timeoutMs);

      const onReady = (ready: boolean) => {
        window.clearTimeout(timer);
        resolve(ready);
      };

      this.apnsTokenWaiters.push(onReady);
    });
  }

  private async logPushPermissionState(): Promise<void> {
    try {
      const platform = this.getPlatform();
      if (platform === 'web') {
        console.log('[FCM] Web notification permission:', Notification.permission);
        return;
      }

      const permissions = await FirebaseMessaging.checkPermissions();
      console.log('[FCM] Native push permission:', {
        platform,
        receive: permissions.receive,
      });
    } catch (error) {
      console.warn('[FCM] Kunne ikke læse push-tilladelser:', error);
    }
  }

  private initializeMessaging() {
    if (this.messagingInitialized) {
      return;
    }

    try {
      this.registerNotificationListeners();
      this.messagingInitialized = true;

      void this.logPushPermissionState();

      setTimeout(() => {
        this.registerNewToken().catch(error => {
          console.error('[FCM] Token registration failed:', error);
        });
      }, 5000);
    } catch (error) {
      console.error('[FCM] Error initializing messaging:', error);
      this.messagingInitialized = false;
    }
  }

  async getFcmToken(): Promise<string | null> {
    try {
      const platform = this.getPlatform();
      const hasPermission = await this.requestNotificationPermission();

      if (!hasPermission) {
        console.warn('[FCM] No notification permission');
        return null;
      }

      if (platform === 'ios') {
        const apnsReady = await this.waitForApnsToken();
        if (!apnsReady) {
          return null;
        }
      }

      let retries = 5;
      while (retries > 0) {
        try {
          let token: string | undefined;

          if (platform === 'web') {
            const swReady = await this.waitForServiceWorker();
            if (!swReady) {
              console.warn('[FCM] Service worker not ready');
              return null;
            }

            const registration =
              (await navigator.serviceWorker.getRegistration('/')) ??
              (await navigator.serviceWorker.getRegistration('/combined-sw.js'));
            if (!registration) {
              console.warn('[FCM] No service worker registration found');
              return null;
            }

            const result = await FirebaseMessaging.getToken({
              vapidKey: firebaseConfig.vapidKey,
              serviceWorkerRegistration: registration
            });
            token = result.token;
          } else {
            const result = await FirebaseMessaging.getToken();
            token = result.token;
          }

          if (!token) {
            throw new Error('No FCM token received');
          }

          return token;
        } catch (error) {
          retries--;
          if (retries === 0) {
            throw error;
          }
          const delayMs = platform === 'ios' ? 2000 : 1000;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }

      return null;
    } catch (error) {
      console.error('[FCM] Error getting FCM token:', error);
      return null;
    }
  }

  private async registerTokenWithServer(token: string): Promise<void> {
    this.store.dispatch(AuthActions.addFcmToken({ token }));
  }

  async registerNewToken() {
    try {

      const platform = this.getPlatform();
      if (platform === 'web') {
        const swReady = await this.waitForServiceWorker();
        if (!swReady) {
          return;
        }
      }

      const token = await this.getFcmToken();

      if (token) {
        await this.registerTokenWithServer(token);
      }
    } catch (error) {
      console.error('[FCM] Error during token registration:', error);
    }
  }

  registerFcmToken(token: string, deviceIdentifier: string): Observable<any> {
    const platform = this.getPlatform();

    return from(this.authService.getToken()).pipe(
      mergeMap(authToken => {
        if (!authToken) {
          console.warn('[FCM] No auth token available - skipping server registration');
          return of({ status: 'skipped', reason: 'no_auth_token' });
        }

        return this.http.post(`${this.API_URL}/users/add-fcm-token`, {
          fcm_token: token,
          device_identifier: deviceIdentifier,
          platform
        }, {
          withCredentials: true,
          headers: {
            'Authorization': `Bearer ${authToken}`,
            ...this.nativeAuthStorage.getClientHeaders()
          }
        }).pipe(
          tap({
            error: (error) => console.error('[FCM] Token registration failed:', error)
          })
        );
      })
    );
  }

  sendPushNotification(pushToken: string | string[], title: string, message: string): Observable<any> {
    const tokens = Array.isArray(pushToken) ? pushToken : [pushToken];

    return this.http.post(`${this.API_URL}/push/send-push-notifications`, {
      tokens,
      title,
      body: message,
      webPush: {
        notification: {
          title,
          body: message,
          icon: '/assets/icons/icon-72.webp',
          badge: '/assets/icons/icon-72.webp'
        }
      }
    }, { withCredentials: true });
  }
}
